import environment from '../../../../common/environment';
import { Product } from '../../shop-cart/interfaces/shop-cart.interface';
import { ProductCardData } from '../../../product-card/product-card-display';
import { SHARPEN_PROGRAM_ID } from '../../../upsell/constants/sharpen-page-type.constants';
import { getCartItemFormat } from '../../../../common/utils/cart-util';
import { getPdpLink, isHeCustomStoreProduct, isHePrivateStoreProduct } from '../../../../common/utils/product-util';
import { getProfessionalPdpLink } from '../../../../common/constants/professional-redirects';
import { isNationalFlow, isTunnelPurchaseFlow, reverseCatalogMap } from '../../../../common/utils/util';

/**
 * Returns the copy right year as a string for the product if it is valid.
 *
 * @param product
 */
export function getCopyRightYear(product: Product): string {
  return product.productMetadata.copyrightYear && product.productMetadata.copyrightYear > 0
    ? '' + product.productMetadata.copyrightYear
    : '';
}

/**
 * Returns the custom store PDP link for the product.
 *
 * @param product
 * @param store
 */
export function getCustomStorePdpLink(product: Product, store?: string): string {
  const isbn = (product.productIdentifier.isbn13 && product.productIdentifier.isbn13 !== "N/A")
    ? product.productIdentifier.isbn13
    : (product.productIdentifier.isbn10 && product.productIdentifier.isbn10 !== "N/A")
      ? product.productIdentifier.isbn10
      : product.productIdentifier.mhid;
  let link = '';
  // if store return PreK-12 Storefront PDP link else national pdp link
  if (store) {
    link = new URL(
      `/prek-12/custom/store/${store}/product/${product.productIdentifier.mhid}.html`,
      environment().mheducationUrl
    ).href;
  } else if (isbn && (isHeCustomStoreProduct(product) || isHePrivateStoreProduct(product))) {
    link = getPdpLink({
      businessUnit: reverseCatalogMap(product.productIdentifier.businessUnit),
      isbn,
      productType: product.productIdentifier.productType?.toUpperCase(),
    });
  } else if (isbn) {
    link = getPdpLink({
      businessUnit: reverseCatalogMap(product.productIdentifier.businessUnit),
      isbn,
      programSourceName: product.productIdentifier.programSourceName
    });
  }
  return link;
}

/**
 * Returns the format text value for the product.
 *
 * @param product
 */
export function getFormatText(product: Product): string {
  if (isHE(product) || isProfessional(product)) {
    return getCartItemFormat(
      product.productMetadata.format,
      product.productMetadata.formatCode,
      product.productMetadata.virtual
    );
  }

  return ""
}

/**
 * Returns the product custom price if it exists (i.e. if it is not undefined or null), or the net price otherwise.
 *
 * Products can have a price equal to zero in case of free trial.
 *
 * @param product
 * @private
 */
// (TODO): It is incorrect, it should fallback to netPrice without additional rules
// It's working today because the free trial cases where it will be used has same
// list and net price, but it's not guaranteed to be the case in the future.
export function getPrice(product: Product): number {
  return product.priceInfo.customPrice ?? product.priceInfo.netPrice;
}

/**
 * Returns the product link based on the product business unit.
 *
 * @param product
 * @param store
 */
export function getProductLink(product: Product, store?: string): string {
  if (isProfessional(product)) {
    return getProfessionalPdpLink(
      product.productIdentifier.programSourceId ?? '',
      product.productIdentifier.programSourceName ?? '',
      product.productIdentifier.isbn13
    );
  }

  return getCustomStorePdpLink(product, store);
}

/**
 * Returns true if the product is a HE product, false otherwise.
 *
 * @param product
 */
export function isHE(product: Product) {
  return product.productIdentifier.businessUnit == 'HE';
}

/**
 * Returns true if the product is a professional product, false otherwise.
 *
 * @param product
 */
export function isProfessional(product: Product) {
  return product.productIdentifier.businessUnit == 'PROFESSIONAL';
}

/**
 * Returns true if the product is a SEG product, false otherwise.
 *
 * @param product
 */
export function isSEG(product: Product) {
  return product.productIdentifier.businessUnit == 'SEG';
}

export function checkCartContainsSharpenPlusAndIsNationalOrTunnel(products: Array<ProductCardData>) {
  const hasSharpenPlusInCart = isProductPresentInCart(products, SHARPEN_PROGRAM_ID);
  return (isNationalFlow() || isTunnelPurchaseFlow()) && hasSharpenPlusInCart;
}

function isProductPresentInCart(products: Array<ProductCardData>, programId: string) {
  return products &&
    products?.some(
      (product) =>
        product?.programSourceId === programId ||
        product?.programId === programId ||
        product?.id === programId
    );
}
